import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useEditPage } from "@/hooks/useEditPage";
import DestinationPicker from "@/components/common/DestinationPicker";
import ErrorBanner from "@/components/common/ErrorBanner";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { Controller } from "react-hook-form";

interface WikiForm {
  title: string;
  destinationId: string | null;
  category: string;
  body: string;
  status: string;
}

export default function WikiEditPage() {
  const { t } = useTranslation();
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<WikiForm>({
    resourceKey: "wiki-page",
    listKey: "wiki",
    apiPath: "/api/admin/wiki",
    defaultValues: {
      title: "",
      destinationId: null,
      category: "general",
      body: "",
      status: "draft",
    },
    navigateTo: "/wiki",
    transformOnLoad: (w) => ({
      title: w.title || "",
      destinationId: w.destinationId || null,
      category: w.category || "general",
      body: w.body || "",
      status: w.status || "draft",
    }),
    transformOnSave: (f) => ({ ...f, destinationId: f.destinationId || null }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/wiki" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? t("content.wiki.form.newTitle") : t("content.wiki.form.editTitle")}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? t("content.wiki.form.newSubtitle")
                : t("content.wiki.form.editSubtitle", { title: itemData?.title })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          <button type="submit" form="wiki-form" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" /> {t("common.actions.save")}
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />

      <form id="wiki-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card space-y-4">
          <div>
            <label className="label">{t("common.fields.title")}</label>
            <input
              {...register("title", { required: t("content.shared.titleRequired") })}
              className="input"
              placeholder={t("content.wiki.form.titlePlaceholder")}
            />
            {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="destinationId" className="label">
                {t("common.fields.destination")}
              </label>
              <Controller
                name="destinationId"
                control={control}
                render={({ field }) => (
                  <DestinationPicker
                    id="destinationId"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div>
              <label className="label">{t("content.wiki.form.categoryLabel")}</label>
              <select {...register("category")} className="input">
                <option value="general">{t("content.wiki.categories.general")}</option>
                <option value="playbook">{t("content.wiki.categories.playbook")}</option>
                <option value="contacts">{t("content.wiki.categories.contacts")}</option>
                <option value="sop">{t("content.wiki.categories.sop")}</option>
              </select>
            </div>
            <div>
              <label className="label">{t("common.fields.status")}</label>
              <select {...register("status")} className="input">
                <option value="draft">{t("common.status.draft")}</option>
                <option value="published">{t("common.status.published")}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label">{t("content.wiki.form.contentLabel")}</label>
            <Controller
              name="body"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder={t("content.wiki.form.contentPlaceholder")}
                />
              )}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
